import React from 'react';
import { Box } from '@mui/material';

interface QuiltVisualizationProps {
  blocksAcross: number;
  blocksDown: number;
  quiltWidth?: number;
  quiltHeight?: number;
  hasSashing?: boolean;
  hasCornerstones?: boolean;
  blockSize?: { width: number; height: number };
  sashingSize?: number;
  borderSizes?: number[];
  borderSize?: number; // For backward compatibility
  maxThumbnailSize?: number; // Optional custom max size
  minThumbnailSize?: number; // Optional custom min size
}

export const QuiltVisualization = ({
  blocksAcross,
  blocksDown,
  quiltWidth,
  quiltHeight,
  hasSashing = false,
  hasCornerstones = false,
  blockSize,
  sashingSize = 0,
  borderSizes = [],
  borderSize = 0,
  maxThumbnailSize = 225,
  minThumbnailSize = 125
}: QuiltVisualizationProps) => {
  // Use multiple border sizes if provided, otherwise fall back to single borderSize for compatibility
  const totalBorderSize = borderSizes.length > 0 ? borderSizes.reduce((sum, size) => sum + size, 0) : borderSize;
  
  // Calculate proportional sizes for visualization
  const getProportionalSizes = (maxThumbSize: number, minThumbSize: number) => {
    if (!blockSize) {
      return { 
        blockWidthPx: 20, 
        blockHeightPx: 20, 
        sashingPx: 3, 
        borderPx: borderSizes.length > 0 ? borderSizes.map(size => Math.max(size * 3, 2)) : [5]
      };
    }

    // Use the provided thumbnail sizes
    const blockWidth = blockSize.width;
    const blockHeight = blockSize.height;
    
    // Calculate total width and height
    let totalWidth, totalHeight;
    if (hasSashing) {
      totalWidth = (blocksAcross * blockWidth) + ((blocksAcross - 1) * sashingSize) + (totalBorderSize * 2);
      totalHeight = (blocksDown * blockHeight) + ((blocksDown - 1) * sashingSize) + (totalBorderSize * 2);
    } else {
      totalWidth = (blocksAcross * blockWidth) + (totalBorderSize * 2);
      totalHeight = (blocksDown * blockHeight) + (totalBorderSize * 2);
    }
    
    // Calculate scale factor based on the larger dimension
    const maxDimension = Math.max(totalWidth, totalHeight);
    let scale = Math.min(maxThumbSize / maxDimension, 1);
    const scaledMaxDimension = maxDimension * scale;
    
    // If scaled dimension is too small, increase scale to meet minimum
    if (scaledMaxDimension < minThumbSize) {
      scale = minThumbSize / maxDimension;
    }
    
    const borderPxArray = borderSizes.length > 0 
      ? borderSizes.map(size => Math.max(size * scale, 2))  
      : [Math.max(totalBorderSize * scale, 3)];

    return {
      blockWidthPx: Math.max(blockWidth * scale, 2), // Minimum 8px for visibility
      blockHeightPx: Math.max(blockHeight * scale, 2), // Minimum 8px for visibility
      sashingPx: Math.max(sashingSize * scale, 2), // Minimum 2px for visibility
      borderPx: borderPxArray
    };
  };

  const { blockWidthPx, blockHeightPx, sashingPx, borderPx } = getProportionalSizes(maxThumbnailSize, minThumbnailSize);

  // Create visualization elements based on sashing/cornerstone configuration
  const createQuiltGrid = () => {
    if (!hasSashing) {
      // Simple grid of blocks without sashing or border
      const blocks = [];
      for (let row = 0; row < blocksDown; row++) {
        for (let col = 0; col < blocksAcross; col++) {
          blocks.push(
            <Box 
              key={`block-${row}-${col}`} 
              sx={{ 
                backgroundColor: 'secondary.light',
                border: 1,
                borderColor: 'secondary.main',
                width: `${blockWidthPx}px`, 
                height: `${blockHeightPx}px`
              }}
            />
          );
        }
      }
      
      const gridContent = (
        <Box 
          sx={{
            display: 'grid',
            backgroundColor: 'background.paper',
            borderRadius: '2px',
            border: totalBorderSize === 0 ? 1 : 0,
            borderColor: 'secondary.main',
            gridTemplateColumns: `repeat(${blocksAcross}, ${blockWidthPx}px)`,
            gridTemplateRows: `repeat(${blocksDown}, ${blockHeightPx}px)`,
            gap: '1px'
          }}
        >
          {blocks}
        </Box>
      );

      // Wrap with borders if needed
      if (totalBorderSize > 0) {
        let wrappedContent = gridContent;
        
        // Apply borders from outside in (reverse order)
        for (let i = borderPx.length - 1; i >= 0; i--) {
          const borderWidth = borderPx[i];
          wrappedContent = (
            <Box 
              sx={{
                backgroundColor: 'common.white',
                border: 1,
                borderColor: 'secondary.main',
                borderRadius: '2px',
                padding: `${borderWidth}px`
              }}
            >
              {wrappedContent}
            </Box>
          );
        }
        
        return wrappedContent;
      }
      
      return gridContent;
    }

    // Complex grid with sashing and optional cornerstones
    const elements = [];
    const gridCols = blocksAcross + (blocksAcross - 1); // blocks + vertical sashing
    const gridRows = blocksDown + (blocksDown - 1); // blocks + horizontal sashing

    // Create template columns array
    const templateCols = [];
    for (let col = 0; col < gridCols; col++) {
      const isBlockCol = col % 2 === 0;
      templateCols.push(isBlockCol ? `${blockWidthPx}px` : `${sashingPx}px`);
    }

    // Create template rows array  
    const templateRows = [];
    for (let row = 0; row < gridRows; row++) {
      const isBlockRow = row % 2 === 0;
      templateRows.push(isBlockRow ? `${blockHeightPx}px` : `${sashingPx}px`);
    }

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const isBlockRow = row % 2 === 0;
        const isBlockCol = col % 2 === 0;
        
        if (isBlockRow && isBlockCol) {
          // This is a block position
          elements.push(
            <Box 
              key={`block-${row}-${col}`} 
              sx={{ 
                backgroundColor: 'secondary.light',
                border: 1,
                borderColor: 'secondary.main',
                width: '100%', 
                height: '100%'
              }} 
            />
          );
        } else if (!isBlockRow && !isBlockCol && hasCornerstones) {
          // This is a cornerstone position
          elements.push(
            <Box 
              key={`cornerstone-${row}-${col}`} 
              sx={{ 
                backgroundColor: 'primary.light',
                border: 1,
                borderColor: 'secondary.main',
                width: '100%', 
                height: '100%'
              }} 
            />
          );
        } else {
          // This is a sashing position
          elements.push(
            <Box 
              key={`sashing-${row}-${col}`} 
              sx={{ 
                backgroundColor: 'background.paper',
                width: '100%', 
                height: '100%' 
              }} 
            />
          );
        }
      }
    }

    const gridContent = (
      <Box 
        sx={{
          display: 'grid',
          backgroundColor: 'background.paper',
          borderRadius: '2px',
          border: totalBorderSize === 0 ? 1 : 0,
          borderColor: 'secondary.main',
          gridTemplateColumns: templateCols.join(' '),
          gridTemplateRows: templateRows.join(' '),
          gap: '0px'
        }}
      >
        {elements}
      </Box>
    );

    // Wrap with borders if needed
    if (totalBorderSize > 0) {
      let wrappedContent = gridContent;
      
      // Apply borders from outside in (reverse order)
      for (let i = borderPx.length - 1; i >= 0; i--) {
        const borderWidth = borderPx[i];
        wrappedContent = (
          <Box 
            sx={{
              backgroundColor: 'common.white',
              border: 1,
              borderColor: 'secondary.main',
              borderRadius: '2px',
              padding: `${borderWidth}px`
            }}
          >
            {wrappedContent}
          </Box>
        );
      }
      
      return wrappedContent;
    }
    
    return gridContent;
  };

  return (
    <div className="inline-block">
      {createQuiltGrid()}
    </div>
  );
};